import React, { useState, useEffect } from "react";
import api, { getFileUrl } from "../../../../api";
import {
 Card,
 Row,
 Col,
 Table,
 Badge,
 Button,
 Modal
} from "react-bootstrap";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";

const STATUS_COLORS = {
 Pending: "#FFC107",
 Approved: "#28A745",
 Rejected: "#DC3545"
};

const HandoverTab = ({ workOrderId }) => {
 const [handovers, setHandovers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [selectedHO, setSelectedHO] = useState(null);
 const [showModal, setShowModal] = useState(false);

 // ==========================
 // FETCH HANDOVER DATA
 // ==========================
 useEffect(() => {
 if (workOrderId) fetchData();
 }, [workOrderId]);

 const fetchData = async () => {
 try {
 setLoading(true);

 const res = await api.get(
 `/handover/work-order/${workOrderId}`
 );

 if (res.data.success) {
 setHandovers(res.data.data || []);
 }
 } catch (err) {
 console.error("Failed to load handovers:", err);
 } finally {
 setLoading(false);
 }
};


 // ==========================
 // PREVIEW PDF
 // ==========================
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);
 const res = await api.get(`/handover/${id}/pdf`, {
 responseType: "blob"
 });
 const url = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );
 window.open(url, "_blank");
 } catch (err) {
 console.error("Handover PDF preview error:", err);
 alert("Failed to preview PDF");
 } finally {
 setPreviewingPdf(false);
 }
 };

 // ==========================
 // DOWNLOAD PDF
 // ==========================
 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/handover/${id}/pdf`, {
 responseType: "blob"
 });
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `Handover-${id}.pdf`;
 link.click();
 } catch (err) {
 alert("Failed to download PDF");
 }
 };

 // ==========================
 // VIEW DETAILS MODAL
 // ==========================
 const handleView = async (handover) => {
 try {
 const res = await api.get(`/handover/${handover._id}`);
 if (res.data.success) {
 setSelectedHO(res.data.data);
 setShowModal(true);
 }
 } catch {
 alert("Failed to fetch details");
 }
 };

 const getStatusBadge = (status) => {
 const map = {
 Pending: "warning",
 Approved: "success",
 Rejected: "danger"
 };
 return map[status] || "secondary";
 };

 // ==========================
 // SUMMARY + CHART DATA
 // ==========================
 const total = handovers.length;
 const approved = handovers.filter((h) => h.status === "Approved").length;
 const pending = handovers.filter((h) => h.status === "Pending").length;
 const rejected = handovers.filter((h) => h.status === "Rejected").length;

 const pieData = [
 { name: "Approved", value: approved },
 { name: "Pending", value: pending },
 { name: "Rejected", value: rejected }
 ];

 if (loading) return <p>Loading handover data...</p>;

 return (
 <>
 <Row className="g-4">
 {/* SUMMARY */}
 <Col md={4}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">Handover Summary</h5>
 <div>Total: <strong>{total}</strong></div>
 <div className="text-success">Approved: <strong>{approved}</strong></div>
 <div className="text-warning">Pending: <strong>{pending}</strong></div>
 <div className="text-danger">Rejected: <strong>{rejected}</strong></div>
 </Card.Body>
 </Card>
 </Col>

 {/* PIE CHART */}
 <Col md={4}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">Status Distribution</h5>
 <PieChart width={260} height={260}>
 <Pie
 data={pieData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={85}
 label
 >
 {pieData.map((e, i) => (
 <Cell key={i} fill={STATUS_COLORS[e.name] || "#999"} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </Card.Body>
 </Card>
 </Col>

 {/* TABLE */}
 <Col md={12}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">Handover Records</h5>
 {handovers.length === 0 ? (
 <div className="text-center text-muted py-3">
 No handovers found for this work order.
 </div>
 ) : (
 <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
 <Table hover className="align-middle">
 <thead className="sticky-top bg-white shadow-sm">
 <tr>
 <th>#</th>
 <th>Handover No</th>
 <th>Status</th>
 <th>BOQ</th>
 <th>Checklist</th>
 <th>Date</th>
 <th>Submitted By</th>
 <th className="text-center" style={{ width: 160 }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {handovers.map((h, i) => (
 <tr key={h._id}>
 <td>{i + 1}</td>
 <td className="text-nowrap font-weight-bold">{h.hoNumber}</td>
 <td><Badge className="rounded-pill" bg={getStatusBadge(h.status)}>{h.status}</Badge></td>
 <td>
 <Badge bg="secondary" className="rounded-pill">
 {h.boqSelected?.length || 0} Items
 </Badge>
 </td>
 <td>
 <Badge bg="info" className="rounded-pill">
 {h.checklist?.length || 0} Checkpoints
 </Badge>
 </td>
 <td className="text-nowrap small">
 {h.submittedDate
 ? new Date(h.submittedDate).toLocaleDateString("en-IN")
 : "N/A"}
 </td>
 <td className="text-nowrap small">{h.submittedBy?.name || "N/A"}</td>
 <td className="text-center">
 <div className="d-flex align-items-center justify-content-center gap-2">
 <Button
 size="sm"
 variant="outline-primary"
 className="border-0"
 onClick={() => handleView(h)}
 title="View Handover Details"
 >
 <i className="fas fa-eye"></i>
 </Button>

 <VerifiedCopyActions 
 id={h._id} 
 type="handover" 
 hasVerifiedCopy={!!h.verifiedCopy?.filePath} 
 verifiedCopy={h.verifiedCopy}
 onUploadSuccess={fetchData}
 canUpload={true}
 />
 </div>
 </td>

 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* DETAILS MODAL */}
 {selectedHO && (
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>Handover Details</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {/* BASIC */}
 <Row className="mb-2">
 <Col md={6}><strong>HO Number:</strong> {selectedHO.hoNumber}</Col>
 <Col md={6}>
 <strong>Status:</strong>{" "}
 <Badge className="rounded-pill" bg={getStatusBadge(selectedHO.status)}>
 {selectedHO.status}
 </Badge>
 </Col>
 </Row>

 {/* PROJECT & DATE */}
 <Row className="mb-2">
 <Col md={6}><strong>Project:</strong> {selectedHO.project?.project}</Col>
 <Col md={6}>
 <strong>Date Submitted:</strong>{" "}
 {selectedHO.submittedDate
 ? new Date(selectedHO.submittedDate).toLocaleDateString()
 : "N/A"}
 </Col>
 </Row>

 {/* BOQ */}
 {selectedHO.boqSelected?.length > 0 && (
 <div className="mb-3">
 <h5>BOQ Items</h5>
 <Table bordered size="sm">
 <thead>
 <tr>
 <th>Item</th>
 <th>Description</th>
 <th>Qty</th>
 <th>Unit</th>
 </tr>
 </thead>
 <tbody>
 {selectedHO.boqSelected.map((b, idx) => (
 <tr key={idx}>
 <td>{b.itemName || b.boqName || "-"}</td>
 <td>{b.description || "-"}</td>
 <td>{b.quantity ?? b.qty ?? "-"}</td>
 <td>{b.unit || "-"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}

 {/* CHECKLIST */}
 {selectedHO.checklist?.length > 0 && (
 <div className="mb-3">
 <h5>Checklist</h5>
 <Table bordered size="sm">
 <thead>
 <tr>
 <th>Item</th>
 <th>Status</th>
 <th>Remarks</th>
 </tr>
 </thead>
 <tbody>
 {selectedHO.checklist.map((c, idx) => (
 <tr key={idx}>
 <td>{c.item}</td>
 <td><Badge className="rounded-pill" bg={getStatusBadge(c.status)}>{c.status}</Badge></td>
 <td>{c.remarks || "-"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}

 {/* DOCUMENTS */}
 {(() => {
 const docs = Array.isArray(selectedHO.handoverDocuments)
 ? selectedHO.handoverDocuments
 : selectedHO.handoverDocuments
 ? [selectedHO.handoverDocuments]
 : [];

 return docs.length > 0 && (
 <div className="mb-3">
 <h5>Documents</h5>
 <ul className="list-group">
 {docs.map((doc, i) => (
 <li key={i} className="list-group-item d-flex justify-content-between">
 {doc.fileName}
 <a href={getFileUrl(doc.fileUrl)} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary">
 View
 </a>
 </li>
 ))}
 </ul>
 </div>
 );
 })()}

 {/* SIGNATURE */}
 {selectedHO.clientSignature?.fileUrl && (
 <div className="mb-3">
 <h5>Client Signature</h5>
 <img
 src={getFileUrl(selectedHO.clientSignature.fileUrl)}
 alt="Signature"
 style={{ maxWidth: 250, border: "2px solid #aaa", padding: 8, borderRadius: 8 }}
 />
 </div>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
 </Modal.Footer>
 </Modal>
 )}

 {/* PREVIEW LOADER */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{ background: "rgba(0,0,0,0.45)", zIndex: 9999 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing Handover PDF…</h6>
 </div>
 </div>
 )}
 </>
 );
};

export default HandoverTab;
